import React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ChatInterfaceProvider } from "@/contexts/ChatInterfaceContext";

/**
 * POSIT APP MAIN ROUTER CONFIGURATION
 * 
 * This file defines the complete routing structure for the Posit AI application.
 * 
 * KEY ROUTING PATTERNS:
 * 1. Embedded Routes (/embed/*): Standalone chat widgets for external websites
 * 2. Chat Routes (/chat/*): Full-screen chat interfaces without main navigation
 * 3. Main App Routes (/): All standard pages with navigation and auth
 * 
 * AUTHENTICATION FLOWS:
 * - requireAuth: Forces login for protected routes
 * - Optional auth: Works for both authenticated and anonymous users
 * - Route protection: Automatic redirects based on user state
 * 
 * VECTOR PROCESSING:
 * Each chat route connects to the Pinecone vector database for semantic search
 * and contextual AI responses based on the Posit's knowledge base.
 */
import Index from "@/pages/Index";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Terms from "@/pages/Terms";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import NotFound from "@/pages/NotFound";
import Root from "@/pages/Root";
import EditProfile from "@/pages/EditProfile";
import MyPosits from "@/pages/MyPosits";
import CreatePosit from "@/pages/CreatePosit";
import Dashboard from "@/pages/Dashboard";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import IntegrationsDashboard from "@/pages/IntegrationsDashboard";
import DashboardSubscriptionManagement from "@/components/dashboard/SubscriptionManagement";
import BugReport from "@/pages/BugReport";
import FeatureRequests from "@/pages/FeatureRequests";
import PositDetails from "@/pages/PositDetails";
import PublicPosits from "@/pages/PublicPosits";
import MyPosit from "@/pages/MyPosit";
import ChatDetails from "@/pages/ChatDetails";
import ChatHistory from "@/pages/ChatHistory";
import CreatorDashboard from "@/pages/CreatorDashboard";
import UserProfile from "@/pages/UserProfile";
import EmbedPosit from "@/pages/EmbedPosit";
import EmbeddedOnly from "@/pages/EmbeddedOnly";
import MinimalEmbed from "@/pages/MinimalEmbed";
import PositTaskFlag from "@/pages/PositTaskFlag";
import ChatWithPosit from "@/pages/ChatWithPosit";
import ChatStarter from "@/pages/ChatStarter";
import UpdatePosit from "@/pages/UpdatePosit";
// Removed marketing pages (temporarily disabled)
import ChatInterfaceDemo from "@/pages/ChatInterfaceDemo";
import PromptTester from "@/pages/PromptTester";
import VoiceChat from "@/pages/VoiceChat";
import VoiceChatOpenAI from "@/pages/VoiceChatOpenAI";
import VoiceChatComparison from "@/pages/VoiceChatComparison";
import { VoiceUpdate } from "@/pages/VoiceUpdate";
import { Navigate } from "react-router-dom";

import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ErrorBoundary from "@/components/ErrorBoundary";
import IntegrationsPage from "@/pages/integrations";
import GoogleCallback from "@/pages/integrations/google-callback";
import AITestingDashboard from "@/pages/AITestingDashboard";
import AdminRoute from '@/components/admin/AdminRoute';
import ConversationMonitorDashboard from "@/pages/ConversationMonitorDashboard";
import { AffiliateDashboard } from "@/components/affiliate/AffiliateDashboard";
import PositWikiPage from "@/pages/PositWikiPage";
import PublicWikiPage from "@/pages/PublicWikiPage";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { STRIPE_PRICE_IDS } from "@/constants/pricing";
import AdminBusinessDashboard from '@/pages/admin/AdminBusinessDashboard';
import AdminHealthDashboard from '@/pages/admin/AdminHealthDashboard';
import UsersTable from '@/pages/admin/business/UsersTable';
import UserDetail from '@/pages/admin/business/UserDetail';
import PositsTable from '@/pages/admin/business/PositsTable';
import SubscriptionsTable from '@/pages/admin/business/SubscriptionsTable';
import AffiliatesTable from '@/pages/admin/business/AffiliatesTable';
import RevenueOverview from '@/pages/admin/business/RevenueOverview';
import PositDetail from '@/pages/admin/business/PositDetail';
import SubscriptionDetail from '@/pages/admin/business/SubscriptionDetail';
import AffiliateDetail from '@/pages/admin/business/AffiliateDetail';
import ConversationsTable from '@/pages/admin/health/ConversationsTable';
import ConversationDetail from '@/pages/admin/health/ConversationDetail';
import KnowledgeGapsList from '@/pages/admin/health/KnowledgeGapsList';
import VectorAccuracy from '@/pages/admin/health/VectorAccuracy';
import BatchChatTesting from '@/pages/admin/health/BatchChatTesting';
import ErrorLogs from '@/pages/admin/health/ErrorLogs';
import MarketingAnalytics from '@/pages/admin/business/MarketingAnalytics';

const queryClient = new QueryClient();

function App() {
  // Startup validation for Stripe price IDs in development and staging
  useEffect(() => {
    const isProd = window.location.hostname === 'tryposit.com';
    const idsToValidate = [STRIPE_PRICE_IDS.MONTHLY, STRIPE_PRICE_IDS.ANNUAL].filter(Boolean);
    if (isProd || idsToValidate.length === 0) return;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('validate-pricing', {
          body: { priceIds: idsToValidate }
        });
        if (error) {
          console.warn('Pricing validation failed:', error);
          return;
        }
        const invalid = Object.entries((data?.results) || {})
          .filter(([, r]: { valid?: boolean }) => r && typeof r.valid === 'boolean' && !r.valid)
          .map(([id]) => id);
        if (invalid.length > 0) {
          console.warn('Invalid price IDs found:', invalid);
        }
      } catch (error) {
        console.warn('Pricing validation error:', error);
      }
    })();
  }, []);
  const router = createBrowserRouter([
    // EMBEDDED WIDGET ROUTES - No navigation, optimized for external embedding
    {
      path: "/embed/posit/:id",
      element: (
        <ThemeProvider>
          <EmbedPosit />
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    {
      path: "/embedded-only/posit/:id",
      element: (
        <ThemeProvider>
          <EmbeddedOnly />
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    {
      path: "/minimal/posit/:id",
      element: <MinimalEmbed />,
      errorElement: <div>Not found</div>,
    },
    // CHAT ROUTES - Full-screen interfaces for focused conversation
    // These routes don't include main navigation to maximize chat space
    {
      path: "/chat",
      element: (
        <ThemeProvider>
          <AuthProvider requireAuth>
            <ChatStarter />
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    {
      path: "/chat/:positId/:chatId?",
      element: (
        <ThemeProvider>
          <AuthProvider>
            <ChatWithPosit />
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    {
      path: "/update/:positId/:chatId?",
      element: (
        <ThemeProvider>
          <AuthProvider requireAuth>
            <UpdatePosit />
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    // MAIN APPLICATION ROUTES - Full navigation and feature access
    {
      path: "/",
      element: (
        <ThemeProvider>
          <AuthProvider>
            <Root />
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
      children: [
        {
          index: true,
          element: <Index />,
        },
        {
          path: "login",
          element: <Login />,
        },
        {
          path: "signup",
          element: <Signup />,
        },
        {
          path: "terms",
          element: <Terms />,
        },
        {
          path: "privacy",
          element: <PrivacyPolicy />,
        },
        // Marketing and comparison routes temporarily removed
        // Dashboard routes (now with main navigation)
        {
          path: "dashboard",
          element: (
            <AuthProvider requireAuth>
              <Dashboard />
            </AuthProvider>
          ),
        },
        {
          path: "dashboard/posits",
          element: (
            <AuthProvider requireAuth>
              <MyPosits />
            </AuthProvider>
          ),
        },
        {
          path: "dashboard/create",
          element: (
            <AuthProvider requireAuth>
              <CreatePosit />
            </AuthProvider>
          ),
        },
        {
          path: "dashboard/analytics",
          element: (
            <AuthProvider requireAuth>
              <AnalyticsDashboard />
            </AuthProvider>
          ),
        },
        {
          path: "dashboard/settings",
          element: (
            <AuthProvider requireAuth>
              <EditProfile />
            </AuthProvider>
          ),
        },
        {
          path: "dashboard/subscriptions",
          element: (
            <AuthProvider requireAuth>
              <DashboardSubscriptionManagement />
            </AuthProvider>
          ),
        },
        {
          path: "dashboard/integrations",
          element: (
            <AuthProvider requireAuth>
              <IntegrationsDashboard />
            </AuthProvider>
          ),
        },
        {
          path: "dashboard/affiliate",
          element: (
            <AuthProvider requireAuth>
              <AffiliateDashboard />
            </AuthProvider>
          ),
        },
        {
          path: "settings",
          element: <Navigate to="/dashboard/settings" replace />,
        },
        {
          path: "chat-history",
          element: (
            <AuthProvider requireAuth>
              <ChatHistory />
            </AuthProvider>
          ),
        },
        {
          path: "creator-dashboard",
          element: (
            <AuthProvider requireAuth>
              <CreatorDashboard />
            </AuthProvider>
          ),
        },
        // Legacy routes for backwards compatibility (with main navigation)
        {
          path: "posits",
          element: <Navigate to="/dashboard/posits" replace />,
        },
        {
          path: "create-posit",
          element: <Navigate to="/dashboard/create" replace />,
        },
        {
          path: "analytics",
          element: <Navigate to="/dashboard/analytics" replace />,
        },
        // Other existing routes
        {
          path: "chat-details/:chatId",
          element: (
            <AuthProvider requireAuth>
              <ChatDetails />
            </AuthProvider>
          ),
        },
        {
          path: "posits/:positId",
          element: (
            <AuthProvider requireAuth>
              <MyPosit />
            </AuthProvider>
          ),
        },

        {
          path: "posit/:positId",
          element: <Navigate to="/dashboard" replace />,
        },
        {
          path: "my-posit",
          element: <Navigate to="/dashboard" replace />,
        },
        {
          path: "p/:handle",
          element: <UserProfile />,
        },
        {
          path: "public-posits",
          element: <PublicPosits />,
        },
        {
          path: "edit-profile",
          element: <Navigate to="/dashboard/settings" replace />,
        },
        {
          path: "bug-reports",
          element: (
            <AuthProvider requireAuth>
              <BugReport />
            </AuthProvider>
          ),
        },
        {
          path: "feature-requests",
          element: (
            <FeatureRequests />
          ),
        },
        {
          path: "posit-details/:positId",
          element: (
            <AuthProvider requireAuth>
              <PositDetails />
            </AuthProvider>
          ),
        },
        {
          path: "posit-tasks/:id",
          element: (
            <AuthProvider requireAuth>
              <PositTaskFlag />
            </AuthProvider>
          ),
        },
        {
          path: "auth/callback",
          element: <Login />,
        },
        {
          path: "integrations",
          element: (
            <AuthProvider requireAuth>
              <IntegrationsPage />
            </AuthProvider>
          ),
        },
        {
          path: "integrations/google-callback",
          element: (
            <AuthProvider>
              <GoogleCallback />
            </AuthProvider>
          ),
        },
        {
          path: "subscription-management",
          element: (
            <AuthProvider requireAuth>
              <DashboardSubscriptionManagement />
            </AuthProvider>
          ),
        },
        // New Admin dashboards (role-guarded)
        { path: "admin/business", element: (<AuthProvider requireAuth><AdminRoute><AdminBusinessDashboard /></AdminRoute></AuthProvider>) },
        { path: "admin/business/users", element: (<AuthProvider requireAuth><AdminRoute><UsersTable /></AdminRoute></AuthProvider>) },
        { path: "admin/business/users/:id", element: (<AuthProvider requireAuth><AdminRoute><UserDetail /></AdminRoute></AuthProvider>) },
        { path: "admin/business/marketing", element: (<AuthProvider requireAuth><AdminRoute><MarketingAnalytics /></AdminRoute></AuthProvider>) },
        { path: "admin/business/posits", element: (<AuthProvider requireAuth><AdminRoute><PositsTable /></AdminRoute></AuthProvider>) },
        { path: "admin/business/subscriptions", element: (<AuthProvider requireAuth><AdminRoute><SubscriptionsTable /></AdminRoute></AuthProvider>) },
        { path: "admin/business/subscriptions/:id", element: (<AuthProvider requireAuth><AdminRoute><SubscriptionDetail /></AdminRoute></AuthProvider>) },
        { path: "admin/business/affiliates", element: (<AuthProvider requireAuth><AdminRoute><AffiliatesTable /></AdminRoute></AuthProvider>) },
        { path: "admin/business/affiliates/:id", element: (<AuthProvider requireAuth><AdminRoute><AffiliateDetail /></AdminRoute></AuthProvider>) },
        { path: "admin/business/revenue", element: (<AuthProvider requireAuth><AdminRoute><RevenueOverview /></AdminRoute></AuthProvider>) },
        { path: "admin/business/posits/:id", element: (<AuthProvider requireAuth><AdminRoute><PositDetail /></AdminRoute></AuthProvider>) },
        { path: "admin/health", element: (<AuthProvider requireAuth><AdminRoute><AdminHealthDashboard /></AdminRoute></AuthProvider>) },
        { path: "admin/health/conversations", element: (<AuthProvider requireAuth><AdminRoute><ConversationsTable /></AdminRoute></AuthProvider>) },
        { path: "admin/health/conversations/:id", element: (<AuthProvider requireAuth><AdminRoute><ConversationDetail /></AdminRoute></AuthProvider>) },
        { path: "admin/health/knowledge-gaps", element: (<AuthProvider requireAuth><AdminRoute><KnowledgeGapsList /></AdminRoute></AuthProvider>) },
        { path: "admin/health/vector-accuracy", element: (<AuthProvider requireAuth><AdminRoute><VectorAccuracy /></AdminRoute></AuthProvider>) },
        { path: "admin/health/batch-testing", element: (<AuthProvider requireAuth><AdminRoute><BatchChatTesting /></AdminRoute></AuthProvider>) },
        { path: "admin/health/errors", element: (<AuthProvider requireAuth><AdminRoute><ErrorLogs /></AdminRoute></AuthProvider>) },
        // Legacy /admin -> redirect to /admin/business
        { path: "admin", element: (<AuthProvider requireAuth><AdminRoute><Navigate to="/admin/business" replace /></AdminRoute></AuthProvider>) },
      ],
    },
    {
      path: "/chat-interface-demo",
      element: (
        <ThemeProvider>
          <AuthProvider>
            <ChatInterfaceDemo />
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    // Legacy chat routes for backwards compatibility
    {
      path: "/chat-with-posit/:positId/:conversationId?",
      element: (
        <ThemeProvider>
          <AuthProvider>
            <ChatWithPosit />
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    // Voice chat route (ElevenLabs - original)
    {
      path: "/chat/voice/:positId",
      element: (
        <ThemeProvider>
          <AuthProvider>
            <ChatInterfaceProvider>
              <VoiceChat />
            </ChatInterfaceProvider>
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    // Voice chat route (OpenAI - new alternative)
    {
      path: "/chat/voice-openai/:positId",
      element: (
        <ThemeProvider>
          <AuthProvider>
            <ChatInterfaceProvider>
              <VoiceChatOpenAI />
            </ChatInterfaceProvider>
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    // Voice chat comparison page
    {
      path: "/chat/voice-compare/:positId",
      element: (
        <ThemeProvider>
          <AuthProvider>
            <ChatInterfaceProvider>
              <VoiceChatComparison />
            </ChatInterfaceProvider>
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    {
      path: "/update-posit/:positId",
      element: (
        <ThemeProvider>
          <AuthProvider requireAuth>
            <UpdatePosit />
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    {
      path: "/update/voice/:positId",
      element: (
        <ThemeProvider>
          <AuthProvider requireAuth>
            <VoiceUpdate />
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    // Wiki page route - standalone without top navigation
    {
      path: "/details/:positId",
      element: (
        <ThemeProvider>
          <AuthProvider requireAuth>
            <ChatInterfaceProvider>
              <PositWikiPage />
            </ChatInterfaceProvider>
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    // Public detail routes (SEO/GEO) - with optional slug
    // New canonical route
    {
      path: "/d/:positId/:slug?",
      element: (
        <ThemeProvider>
          <AuthProvider>
            <ChatInterfaceProvider>
              <PublicWikiPage />
            </ChatInterfaceProvider>
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    // Backward-compatible route
    {
      path: "/w/:positId/:slug?",
      element: (
        <ThemeProvider>
          <AuthProvider>
            <ChatInterfaceProvider>
              <PublicWikiPage />
            </ChatInterfaceProvider>
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
    // Legacy posits route
    {
      path: "/posits/u/:handle",
      element: (
        <ThemeProvider>
          <AuthProvider>
            <MyPosit />
          </AuthProvider>
        </ThemeProvider>
      ),
      errorElement: <NotFound />,
    },
  ]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <Toaster />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
